import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";import {
  Badge,
  Box,
  CircularProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import { useNotifications } from "@/context/NotificationsContext";
import { deleteDealAssignee } from "@/services/notificationsApi";
import { formatNoteDateTime } from "@/utils/formatDate";
import type { Notification } from "@/types/dealTask";
import { CustomIconButton } from "@/components/common/customIconButton";
import { CustomButton } from "@/components/common/customButton";
import { ErrorDialog } from "@/components/common/errorDialog";
import { useErrorDialog } from "@/hooks/useErrorDialog";
import notificationIcon from "@/assets/svg/notification.svg";

const menuPaperSx = {
  width: 320,
  maxWidth: 360,
  borderRadius: "8px",
  boxShadow:
    "0px 1px 5px 0px rgba(44, 62, 80, 0.20), 0px 2px 2px 0px rgba(44, 62, 80, 0.14), 0px 3px 1px 0px rgba(44, 62, 80, 0.12)",
  backgroundColor: "#F7F7F9",
  border: "1px solid #E0E0E0",
  marginTop: "0px",
} as const;

const menuContainerSx = {
  "& .MuiMenu-list": {
    padding: 0,
  },
  "& .MuiPaper-root": menuPaperSx,
} as const;

const menuHeaderSx = {
  padding: "12px 16px",
  backgroundColor: "#182737",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 500,
  borderTopLeftRadius: "8px",
  borderTopRightRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
} as const;

const TASK_ASSIGNED_TITLE = "New Task Assigned to You";
const DEAL_UPDATED_TITLE = "Deal updated";
const DEAL_ASSIGNED_TITLE = "Deal assignment";

function getNotificationTitle(notification: Notification): string {
  switch (notification.type) {
    case "task_assigned":
      return TASK_ASSIGNED_TITLE;
    case "deal_updated":
      return DEAL_UPDATED_TITLE;
    case "deal_assigned":
      return DEAL_ASSIGNED_TITLE;
  }
}

function getNotificationDestination(notification: Notification): string {
  const { deal_id } = notification.payload;

  switch (notification.type) {
    case "task_assigned":
      return `/deals/${deal_id}?taskId=${notification.entity_id}`;
    case "deal_updated":
    case "deal_assigned":
      return `/deals/${deal_id}`;
  }
}

function NotificationMessageBody({
  notification,
}: {
  notification: Notification;
}) {
  switch (notification.type) {
    case "task_assigned": {
      const taskDescription = notification.payload.task_description?.trim();

      return (
        <>
          {taskDescription ? (
            <>
              {notification.message ? " " : null}
              <Box
                component="span"
                sx={{
                  fontWeight: "700 !important",
                  color: "#182737 !important",
                  opacity: 1,
                }}
              >
                {taskDescription}
              </Box>
            </>
          ) : null}
        </>
      );
    }
    case "deal_updated":
    case "deal_assigned": {
      return (
        <Box
          component="span"
          sx={{
            fontWeight: "700 !important",
            color: "#182737 !important",
            opacity: 1,
          }}
        >
          {notification.message}
        </Box>
      );
    }
  }
}

function notificationItemBorderSx(index: number, total: number) {
  return {
    borderBottom: index < total - 1 ? "1px solid #E0E0E0" : "none",
    alignItems: "flex-start",
    py: 1.25,
    px: 2,
    whiteSpace: "normal",
    color: "#0E4664",
    fontFamily: "Helvetica, Neue",
    gap: 0,
    "&:hover": {
      backgroundColor: "rgba(0, 40, 60, 0.1)",
    },
  };
}

export function NotificationBellButton() {
  const { unreadCount } = useNotifications();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <span ref={anchorRef}>
        <CustomIconButton
          tooltip="Notifications"
          onClick={() => setAnchorEl(anchorRef.current)}
          icon={
            <Badge
              badgeContent={unreadCount}
              color="error"
              max={99}
              invisible={unreadCount === 0}
              sx={{
                "& .MuiBadge-badge": {
                  top: 4,
                  right: 4,
                },
              }}
            >
              <Box
                component="img"
                src={notificationIcon}
                alt=""
                sx={{ width: 20, height: 20, display: "block" }}
              />
            </Badge>
          }
        />
      </span>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={menuContainerSx}
      >
        <NotificationMenuPanel
          open={open}
          onNavigate={() => setAnchorEl(null)}
        />
      </Menu>
    </>
  );
}

interface NotificationMenuPanelProps {
  open?: boolean;
  onNavigate?: () => void;
}

export function NotificationMenuPanel({
  open = false,
  onNavigate,
}: NotificationMenuPanelProps) {
  const navigate = useNavigate();
  const {
    notifications,
    candidateNotifications,
    inboxLoading,
    fetchInbox,
    markRead,
    markAllRead,
    refetchUnreadCount,
  } = useNotifications();
  const { errorDialog, showError, showErrorFromResponse, hideError } =
    useErrorDialog();

  useEffect(() => {
    if (open) void fetchInbox(true);
  }, [open, fetchInbox]);

  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      const candidateMatch = candidateNotifications.find(
        (candidate) => candidate.id === notification.id,
      );

      if (
        candidateMatch?.type === "deal_assigned" &&
        candidateMatch.payload.deal_id
      ) {
        try {
          const response = await deleteDealAssignee(
            candidateMatch.payload.deal_id,
          );
          // 404 means the assignee row is already gone, which is the goal.
          if (!response?.success && response?.status !== 404) {
            // Leave the notification unread and stay put so the user can retry.
            showErrorFromResponse(response);
            return;
          }
        } catch (error) {
          console.error("delete deal-assignee", error);
          showError(
            undefined,
            "Failed to acknowledge the deal assignment. Please try again.",
          );
          return;
        }
      }

      await markRead(notification.id);
      onNavigate?.();
      navigate(getNotificationDestination(notification));
      await refetchUnreadCount();
    },
    [
      candidateNotifications,
      markRead,
      navigate,
      onNavigate,
      refetchUnreadCount,
      showError,
      showErrorFromResponse,
    ],
  );

  const handleMarkAllRead = async () => {
    await markAllRead();
  };

  return (
    <>
      <Box sx={menuHeaderSx}>
        <Typography
          sx={{
            fontSize: "14px !important",
            fontWeight: "500 !important",
            color: "#fff !important",
          }}
        >
          Notifications
        </Typography>
      </Box>

      {inboxLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} sx={{ color: "#0E4664" }} />
        </Box>
      ) : notifications.length === 0 ? (
        <MenuItem
          disabled
          sx={{ opacity: 0.7, py: 2, justifyContent: "center" }}
        >
          No unread notifications
        </MenuItem>
      ) : (
        <Box sx={{ maxHeight: 320, overflow: "auto" }}>
          {notifications.map((notification, index) => (
            <MenuItem
              key={notification.id}
              onClick={() => void handleNotificationClick(notification)}
              sx={notificationItemBorderSx(index, notifications.length)}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: "#0E4664",
                  alignSelf: "flex-start",
                  mt: 0.125,
                }}
              >
                <AssignmentOutlined fontSize="small" />
              </ListItemIcon>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: "600 !important",
                    color: "#0E4664",
                    mb: 0.25,
                    lineHeight: 1.3,
                  }}
                >
                  {getNotificationTitle(notification)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    color: "#182737",
                    opacity: 0.75,
                    mb: 0.25,
                    lineHeight: 1.35,
                  }}
                >
                  <NotificationMessageBody notification={notification} />
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#182737",
                    opacity: 0.55,
                  }}
                >
                  {formatNoteDateTime(notification.created_at ?? "")}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Box>
      )}

      {!inboxLoading && notifications.length > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: "1px solid #E0E0E0",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CustomButton
            text="Mark all as read"
            handleButtonPress={() => void handleMarkAllRead()}
            isButtonDisabled={inboxLoading}
            solid={false}
            widthOverride="100%"
          />
        </Box>
      )}

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </>
  );
}
